import { Divider, IconButton, Stack, Typography } from '@mui/joy';
import {
  ArrowCircleLeft,
  ArrowCircleRight,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { ReactNode, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import { TranslateCaseManager } from './TranslateCaseManager';
import { TranslateProgram } from './TranslateProgram';
import { TranslateReportForm } from './TranslateReportForm';

import { useTenantSlug } from '../tenant/helpers';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Radio } from '~/components/ui/radio';
import { Option, SelectField } from '~/components/ui/select';
import { Tab, TabList, TabPanel, Tabs } from '~/components/ui/tabs';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { LocalizationService } from '~/services/api/localization/endpoints';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { TenantService } from '~/services/api/tenant/endpoints';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';

export enum LocalizationFeature {
  BIO = 'Homepage Bio',
  KEY_POINTS = 'Key Points',
  REPORT_FORM_SETTTINGS = 'Report Form Settings',
  CUSTOMIZE_BANNER = 'Customize banner',
  MESSAGE_TEMPLATE = 'Message Template',
  REPORT_TYPE = 'Report Type',
  DEPARTMENT = 'Department',
  CUSTOM_FIELD = 'Custom Field',
  CUSTOM_FIELD_CONDITIONAL_LOGIC = 'Custom Field Conditional Logic',
  CASE_MANAGER_FIELD = 'Case Manager Field',
  FAQ = 'FAQ',
}

const schema = z.object({
  is_original: z.boolean(),
  selected_language: z.coerce.number().nullable(),
  selected_feature: z.string().nullable(),
  selected_custom_field: z.number().nullable(),
  selected_custom_field_conditional_logic: z.number().nullable(),
  selected_case_manager_field: z.number().nullable(),
  is_editing: z.boolean(),
  tenant_id: z.number().nullable(),
  channel_object_id: z.number().nullable(),
  program_tabs_ids: z.array(z.number()),
  department_ids: z.array(z.number()),
  report_type_ids: z.array(z.number()),
  faq_items: z.array(z.number()),
});
type FormFields = z.infer<typeof schema>;
export const useLocalizationForm = () => useFormContext<FormFields>();

export function MultiLanguageSupport({
  children,
  defaultValues = {},
}: {
  readonly children: ReactNode;
  readonly defaultValues?: Partial<FormFields>;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const form = useForm({
    schema,
    defaultValues: {
      is_original: false,
      selected_feature: null,
      selected_custom_field: null,
      selected_custom_field_conditional_logic: null,
      selected_case_manager_field: null,
      channel_object_id: null,
      is_editing: false,
      tenant_id: null,
      program_tabs_ids: [],
      department_ids: [],
      report_type_ids: [],
      faq_items: [],
      ...defaultValues,
    },
  });

  const isOriginal = form.watch('is_original');
  const selectedLanguage = form.watch('selected_language');
  const selectedFeature = form.watch('selected_feature');
  const isEditing = form.watch('is_editing');

  const autoTranslate = useWrappedMutation(
    LocalizationService.endpoints.autoTranslate.useMutation(),
  );

  const isChannelFeatures =
    selectedFeature === LocalizationFeature.CUSTOMIZE_BANNER ||
    selectedFeature === LocalizationFeature.MESSAGE_TEMPLATE ||
    selectedFeature === LocalizationFeature.REPORT_FORM_SETTTINGS ||
    selectedFeature === LocalizationFeature.KEY_POINTS;

  const successToast = () =>
    createToastNotification(
      intl.formatMessage({ defaultMessage: 'Successfully translated' }),
      'success',
    );
  const errorToast = () =>
    createToastNotification(
      intl.formatMessage({ defaultMessage: 'Something went wrong' }),
      'error',
    );

  const updateTenantLocalization = async (data: FormFields) => {
    if (
      data.selected_feature === LocalizationFeature.BIO &&
      data.selected_language &&
      data.tenant_id
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            tenant_ids: [data.tenant_id],
            tenant_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateChannelLocalization = async (data: FormFields) => {
    if (isChannelFeatures && data.selected_language && data.channel_object_id) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            form_type_tenant_management_ids: [data.channel_object_id],
            form_type_tenant_management_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateProgramTabLocalization = async (data: FormFields) => {
    if (
      data.selected_feature?.startsWith('program-tab') &&
      data.selected_language &&
      data.program_tabs_ids
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            program_settings_tab_ids: data.program_tabs_ids,
            program_settings_tab_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateFAQLocalization = async (data: FormFields) => {
    if (
      data.selected_feature === LocalizationFeature.FAQ &&
      data.selected_language &&
      data.faq_items
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            program_settings_tab_faq_ids: data.faq_items,
            program_settings_tab_faq_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateDepartmentLocalization = async (data: FormFields) => {
    if (
      data.selected_language &&
      data.selected_feature === LocalizationFeature.DEPARTMENT &&
      data.department_ids.length > 0
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            department_ids: data.department_ids,
            department_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateReportTypeLocalization = async (data: FormFields) => {
    if (
      data.selected_language &&
      data.selected_feature === LocalizationFeature.REPORT_TYPE &&
      data.report_type_ids.length > 0
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            report_type_ids: data.report_type_ids,
            report_type_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateCustomFieldTranslation = async (data: FormFields) => {
    if (
      data.selected_language &&
      data.selected_feature === LocalizationFeature.CUSTOM_FIELD &&
      data.selected_custom_field
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            report_custom_field_ids: [data.selected_custom_field],
            report_custom_field_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateCustomFieldConditionalLogicTranslation = async (
    data: FormFields,
  ) => {
    if (
      data.selected_language &&
      data.selected_feature ===
        LocalizationFeature.CUSTOM_FIELD_CONDITIONAL_LOGIC &&
      data.selected_custom_field_conditional_logic
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            report_custom_field_conditional_logic_ids: [
              data.selected_custom_field_conditional_logic,
            ],
            report_custom_field_conditional_logic_language_ids: [
              data.selected_language,
            ],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const updateCaseManagerFieldTranslation = async (data: FormFields) => {
    if (
      data.selected_language &&
      data.selected_feature === LocalizationFeature.CASE_MANAGER_FIELD &&
      data.selected_case_manager_field
    ) {
      await autoTranslate.mutateAsync(
        {
          selectedChannel: selectedChannel ?? 0,
          slug: slug,
          payload: {
            case_manager_field_ids: [data.selected_case_manager_field],
            case_manager_field_language_ids: [data.selected_language],
          },
        },
        {
          onSuccess: successToast,
          onError: errorToast,
        },
      );
    }
  };

  const autoTranslateHandler = form.handleSubmit(async (data) => {
    updateTenantLocalization(data);
    updateChannelLocalization(data);
    updateProgramTabLocalization(data);
    updateDepartmentLocalization(data);
    updateFAQLocalization(data);
    updateReportTypeLocalization(data);
    updateCustomFieldTranslation(data);
    updateCaseManagerFieldTranslation(data);
    updateCustomFieldConditionalLogicTranslation(data);
  });

  const isOriginalToggle = () => {
    if (isEditing) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Unable to switch to original when editing',
        }),
        'warning',
      );
      return;
    } else if (!selectedLanguage) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'No language selected',
        }),
        'warning',
      );
      return;
    }
    form.setValue('is_original', !isOriginal);
  };

  return (
    <FormProvider {...form}>
      <Dialog>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent size='full' asChild>
          <Card>
            <form>
              <div className='flex flex-row justify-between items-center !z-50'>
                <Typography level='title-lg'>
                  <FormattedMessage defaultMessage='Multi-language Support' />
                </Typography>
                <div className='flex flex-row items-center gap-4'>
                  <Radio
                    className='items-center'
                    checked={isOriginal}
                    onClick={isOriginalToggle}
                    color='primary'
                    label={
                      <Typography color='primary'>
                        <FormattedMessage defaultMessage='Original' />
                      </Typography>
                    }
                  />
                  <Typography level='body-md'>
                    <FormattedMessage defaultMessage='Translate' />
                  </Typography>
                  <SelectField
                    field='selected_language'
                    control={form.control}
                    className='isolate z-50'
                    disabled={isEditing || autoTranslate.state.isLoading}
                    mountOptionsToRoot
                  >
                    <LanguageOptions />
                  </SelectField>
                  <Button
                    variant='soft'
                    color='neutral'
                    disabled={!selectedFeature || isEditing}
                    loading={autoTranslate.state.isLoading}
                    onClick={autoTranslateHandler}
                  >
                    <FormattedMessage defaultMessage='Auto Translate' />
                  </Button>
                  <DialogClose>
                    <IconButton>
                      <X />
                    </IconButton>
                  </DialogClose>
                </div>
              </div>
              <div>
                <Tabs defaultValue='program'>
                  <TabList className='w-fit'>
                    <Tab value='program'>
                      <FormattedMessage defaultMessage='Program' />
                    </Tab>
                    <Tab value='report-form'>
                      <FormattedMessage defaultMessage='Report Form' />
                    </Tab>
                    <Tab value='case-manager'>
                      <FormattedMessage defaultMessage='Case Manager' />
                    </Tab>
                  </TabList>
                  <TabPanel value='program'>
                    <TranslateProgram />
                  </TabPanel>
                  <TabPanel value='report-form'>
                    <TranslateReportForm />
                  </TabPanel>
                  <TabPanel value='case-manager'>
                    <TranslateCaseManager />
                  </TabPanel>
                </Tabs>
              </div>
            </form>
          </Card>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
}

function LanguageOptions() {
  const tenantQuery = TenantService.endpoints.userTenant.useQuery();
  return (
    <QueryResolver query={tenantQuery}>
      {(data) =>
        data.languages
          .filter((language) => language.id !== data.base_language.id)
          .map((language) => (
            <Option value={language.id} key={language.id}>
              {language.name}
            </Option>
          ))
      }
    </QueryResolver>
  );
}

export function WarningChip({ children }: { readonly children: ReactNode }) {
  const [showWarning, setShowWarning] = useState(true);

  if (!showWarning) return null;

  return (
    <Card
      color='primary'
      variant='soft'
      className='!rounded-3xl border-2 !border-brand-primary-600 !p-4 w-full'
    >
      <div className='flex flex-row gap-2 py-2 items-start'>
        <div className='basis-8'>
          <WarningCircle />
        </div>
        <Typography level='title-sm' color='neutral'>
          {children}
        </Typography>
        <div className='basis-4'>
          <X
            size={20}
            className='cursor-pointer'
            onClick={() => setShowWarning(false)}
          />
        </div>
      </div>
    </Card>
  );
}

export function SideTabMenu({
  title,
  titleIconRight,
  description,
  isActive,
  disabled,
  onClick,
}: {
  readonly title: string;
  readonly titleIconRight?: React.ReactNode;
  readonly description: string;
  readonly isActive: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  return (
    <>
      <div className='flex flex-row justify-center items-center'>
        <div className='flex flex-col gap-2'>
          <Stack direction='row' justifyContent='space-between'>
            <Typography level='title-lg'>{title}</Typography>
            {titleIconRight}
          </Stack>
          <Typography>{description}</Typography>
        </div>
        <div className='flex flex-col gap-2'>
          <IconButton
            onClick={() => {
              if (disabled) {
                createToastNotification(
                  intl.formatMessage({
                    defaultMessage:
                      'Cancel edit mode first in order to switch tab.',
                  }),
                  'warning',
                );
                return;
              }
              onClick();
            }}
          >
            {!isActive ? (
              <ArrowCircleRight size={40} />
            ) : (
              <ArrowCircleLeft
                size={40}
                weight='fill'
                className='text-brand-primary'
              />
            )}
          </IconButton>
        </div>
      </div>
      <Divider />
    </>
  );
}

export function SaveCancelButtons({
  isLoading,
  onSave,
  isEditable = true,
  warningText,
}: Readonly<{
  isLoading: boolean;
  onSave: () => void;
  isEditable?: boolean;
  warningText?: string;
}>) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const form = useLocalizationForm();
  const isOriginal = form.watch('is_original');
  const isEditing = form.watch('is_editing');
  const setIsEditing = () => form.setValue('is_editing', !isEditing);

  const showWarningMessage = () => {
    createToastNotification(
      warningText ??
        intl.formatMessage({
          defaultMessage:
            'No Editable Custom Field Translation found, please click auto translate first to continue',
        }),
      'warning',
    );
  };

  return (
    <>
      {!isEditing ? (
        <Button
          variant='outlined'
          color='neutral'
          size='lg'
          onClick={isEditable ? setIsEditing : showWarningMessage}
          disabled={isOriginal}
        >
          <FormattedMessage defaultMessage='Edit' />
        </Button>
      ) : (
        <div className='flex gap-2'>
          <Button
            variant='outlined'
            color='neutral'
            size='lg'
            loading={isLoading}
            onClick={onSave}
          >
            <FormattedMessage defaultMessage='Save' />
          </Button>
          <Button
            variant='plain'
            color='neutral'
            size='lg'
            onClick={setIsEditing}
            disabled={isLoading}
          >
            <FormattedMessage defaultMessage='Cancel' />
          </Button>
        </div>
      )}
    </>
  );
}
